import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import GoogleReviews from "../components/GoogleReviews";
import BookingButton from "@/components/BookingButton";
import heroBackgroundOptimized from "@/assets/hero-bg-new.jpg";
import bonCadeauTemplate from "@/assets/bon-cadeau-template.png";
import usePageSEO from "@/hooks/usePageSEO";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useNavbarLogo } from "@/contexts/NavbarLogoContext";
import MinimalVideoPlayer from "@/components/MinimalVideoPlayer";
import { useServicesData } from "@/hooks/useServicesData";
import { formatDuration, formatPrice } from "@/utils/serviceFormatters";

const Home = () => {
  const heroLogoRef = useRef<HTMLImageElement>(null);
  const { setHeroLogoVisible } = useNavbarLogo();
  const [videoActivated, setVideoActivated] = useState(false);
  const { services, isLoading: servicesLoading } = useServicesData();

  const normalize = (value: string) =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();

  const findServiceInfo = (name: string) => {
    const target = normalize(name);
    const service = services.find((s) => normalize(s.name) === target);
    if (!service) return null;
    const duration = formatDuration(service.duration);
    const price = formatPrice(service.price);
    if (!duration || !price) return null;
    return `${duration} • ${price}`;
  };

  const FeaturedServiceInfo = ({ name }: { name: string }) => {
    if (servicesLoading) {
      return <div className="mb-2 h-4 w-24 animate-pulse rounded bg-bbz-green/20" />;
    }
    const info = findServiceInfo(name);
    if (!info) return null;
    return <p className="mb-2 text-sm">{info}</p>;
  };
  
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setHeroLogoVisible(entry.isIntersecting);
    }, {
      threshold: 0
    });
    if (heroLogoRef.current) {
      observer.observe(heroLogoRef.current);
    }
    return () => observer.disconnect();
  }, [setHeroLogoVisible]);
  
  usePageSEO({
    title: "Bio Beauté Zen | Institut de beauté bio à Montpellier",
    description: "Bio Beauté Zen, institut de beauté bio à Montpellier. Soins du visage, massages bien-être, épilations naturelles. Prenez rendez-vous pour une expérience zen et authentique.",
    canonical: "https://biobeautezen.com/",
    ogTitle: "Bio Beauté Zen | Soins bio et bien-être à Montpellier",
    ogDescription: "Institut de beauté bio à Montpellier : soins visage, corps, massages et rituels bien-être 100% naturels. Découvrez l'univers de Valérie, esthéticienne passionnée."
  });

  const instituteImages = [{
    src: "/images/institut/institut-6.jpg",
    alt: "Entrée et enseigne Bio Beauté Zen"
  }, {
    src: "/images/institut/institut-2.jpg",
    alt: "Espace accueil institut Bio Beauté Zen"
  }, {
    src: "/images/institut/institut-4.jpg",
    alt: "Ambiance cosy et naturelle"
  }, {
    src: "/images/institut/institut-1.jpg",
    alt: "Ambiance zen et apaisante en cabine"
  }, {
    src: "/images/institut/institut-3.jpg",
    alt: "Produit cire d'épilation Fille au pluriel"
  }, {
    src: "/images/institut/institut-5.jpg",
    alt: "Carte de visite et accueil Bio Beauté Zen"
  }];
  
  return <div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 px-4" aria-labelledby="hero-title">
        <div className="absolute inset-0 z-0 bg-cover bg-center" style={{
        backgroundImage: `url('${heroBackgroundOptimized}')`,
        filter: "blur(1.5px) brightness(0.7)"
      }} role="presentation">
          <div className="absolute inset-0 bg-gradient-to-b from-bbz-green/50 to-bbz-cream/50" />
        </div>

        <div className="container mx-auto text-center max-w-4xl relative z-10">
          <div className="mb-6 flex justify-center">
            <img ref={heroLogoRef} src="/images/logo-bbz-sans-baseline.png" alt="Bio Beauté Zen - Beauté naturelle, Bien-être authentique" className="h-28 md:h-32 lg:h-40 w-auto max-w-full object-contain drop-shadow-2xl" />
          </div>
          <p className="text-lg md:text-xl mb-10 max-w-2xl mx-auto text-white drop-shadow-2xl">
            Votre institut de beauté bio à Montpellier spécialisé dans les soins visage, massages et épilations.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <BookingButton className="btn-primary flex items-center justify-center gap-2">
              Prendre rendez-vous <ArrowRight className="w-5 h-5" />
            </BookingButton>
            <Link to="/soins-visage-massages-epilations-montpellier" className="btn-secondary">
              Découvrir les soins
            </Link>
          </div>
        </div>
      </section>

      {/* Présentation */}
      <section className="section-container" aria-labelledby="presentation-title">
        <div className="max-w-4xl mx-auto">
          <h2 id="presentation-title" className="section-title text-center mx-auto after:mx-auto">
            Soyez la bienvenue chez Bio Beauté Zen
          </h2>
          <p className="text-center mb-10">Offrez-vous un moment de bien-être dans un cadre cocooning et zen :</p>

          <div className="grid grid-cols-1 md:grid-cols-[42%_58%] gap-10 items-stretch">
            <div className="w-full h-full">
              <img src="/images/portrait-valerie.jpg" alt="Portrait de Valérie, esthéticienne chez Bio Beauté Zen à Montpellier" loading="lazy" className="rounded-lg shadow-md w-full h-full md:max-h-96 lg:max-h-none object-cover object-[center_5%]" />
            </div>
            <div className="grid grid-cols-1 gap-10">
              <article className="card bg-bbz-beige/40" aria-label="L'institut Bio Beauté Zen">
                <h3 className="font-poppins text-xl font-medium mb-3">L'institut</h3>
                <p className="mb-4">
                  Valérie vous accueille dans son institut à l'ambiance chaleureuse et minimaliste. Ici, pas de
                  promesses en l'air : je sélectionne des soins bio, sensoriels et efficaces, pensés pour votre peau et
                  votre sérénité.
                </p>
                <Link to="/institut-beaute-bio-montpellier" className="inline-flex items-center text-bbz-text hover:text-bbz-green font-medium transition-colors">
                  En savoir plus <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </article>

              <article className="card bg-bbz-pink/30" aria-label="Engagements écologiques et naturels">
                <h3 className="font-poppins text-xl font-medium mb-3">Engagements</h3>
                <ul className="leaf-bullet mb-4">
                  <li>Proposer une routine de beauté minimaliste et sans artifices
                </li>
                  <li>Respect de votre peau et de sa sensibilité</li>
                  <li>Approche propice à la détente totale</li>
                  <li>Démarche éco-responsable</li>
                </ul>
                <Link to="/soins-visage-massages-epilations-montpellier" className="inline-flex items-center text-bbz-text hover:text-bbz-green font-medium transition-colors">
                  Découvrir nos soins <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Carrousel de l'institut */}
      <section className="w-full pb-16 animate-fade-in">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <Carousel opts={{
          align: "start",
          loop: true,
          skipSnaps: false,
          dragFree: true,
          containScroll: "trimSnaps"
        }} className="w-full">
            <CarouselContent className="-ml-2 md:-ml-4">
              {instituteImages.map((image, index) => <CarouselItem key={index} className="pl-2 md:pl-4 basis-full md:basis-1/2 lg:basis-1/4">
                  <div className="relative overflow-hidden rounded-lg shadow-md">
                    <img src={image.src} alt={image.alt} className="w-full aspect-square object-cover select-none" loading="lazy" draggable="false" />
                  </div>
                </CarouselItem>)}
            </CarouselContent>

            <CarouselPrevious className="hidden md:flex -left-4 lg:-left-12 bg-background/90 hover:bg-bbz-green/20 border-bbz-green text-bbz-text" />
            <CarouselNext className="hidden md:flex -right-4 lg:-right-12 bg-background/90 hover:bg-bbz-green/20 border-bbz-green text-bbz-text" />
          </Carousel>
        </div>
      </section>

      {/* Soins Vedettes */}
      <section className="bg-bbz-beige/30 py-16" aria-labelledby="soins-vedettes-title">
        <div className="container mx-auto px-4">
          <h2 id="soins-vedettes-title" className="section-title text-center mx-auto after:mx-auto">
            Mes soins favoris, bientôt les vôtres
          </h2>
          <p className="text-center mb-10">
            Je les ai appris avec passion, testés avec plaisir, et je les pratique avec cœur.
            <br />
            Voici les soins que j'aime profondément vous faire vivre en cabine :
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <article className="card hover:scale-105 transition-transform duration-300" aria-label="Soin visage Bio Éclat">
              <h3 className="font-poppins text-xl font-medium mb-2">Soin visage hydratant relaxant BIO</h3>
              <FeaturedServiceInfo name="Soin visage Hydratant Relaxant BIO" />
              <p>Un soin complet qui illumine votre teint grâce aux actifs végétaux régénérants.</p>
            </article>

            <article className="card hover:scale-105 transition-transform duration-300" aria-label="Massage Zen Harmonie">
              <h3 className="font-poppins text-xl font-medium mb-2">Massage corps SERENITE BIO soin Signature</h3>
              <FeaturedServiceInfo name="Massage corps SERENITE BIO soin Signature" />
              <p>Un massage relaxant aux huiles essentielles bio pour une détente profonde.</p>
            </article>

            <article className="card hover:scale-105 transition-transform duration-300" aria-label="Gommage Corps Fleur de Coton">
              <h3 className="font-poppins text-xl font-medium mb-2">Massage détente des pieds - EVASION en Inde</h3>
              <FeaturedServiceInfo name="Massage détente des pieds - Evasion en Inde" />
              <p>Un massage inspiré de l'Ayurveda pour détendre les pieds et apaiser les tensions.</p>
            </article>
          </div>

          <div className="text-center mt-10">
            <Link to="/soins-visage-massages-epilations-montpellier" className="btn-primary">
              Voir la carte complète
            </Link>
          </div>
        </div>
      </section>

      {/* Bon Cadeau */}
      <section className="section-container" aria-labelledby="bon-cadeau-title">
        <div className="rounded-lg p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="md:w-3/5 mb-6 md:mb-0">
            <h2 id="bon-cadeau-title" className="section-title">
              Offrez du bien-être
            </h2>
            <p className="mb-6">
              Faites plaisir à vos proches avec un bon cadeau Bio Beauté Zen. Une attention délicate pour offrir un
              moment de détente et de beauté naturelle.
            </p>
            <Link to="/bon-cadeaux-estheticienne-bio-montpellier" className="btn-primary">Offrir un bon cadeau</Link>
          </div>
          <div className="md:w-2/5 flex justify-center">
            <img src={bonCadeauTemplate} alt="Bon cadeau Bio Beauté Zen" className="h-24 w-56 md:h-36 md:w-80 max-w-full rounded-lg rotate-3 shadow-2xl drop-shadow-lg object-contain transition-transform duration-700 ease-in-out hover:scale-110 hover:-rotate-2 transform-gpu" />
          </div>
        </div>
      </section>

      {/* Témoignages */}
      <section className="bg-bbz-green/20 py-16" aria-labelledby="temoignages-title">
        <div className="container mx-auto px-4">
          <h2 id="temoignages-title" className="section-title text-center mx-auto after:mx-auto">
            Ce que vous avez pensé de votre soin
          </h2>

          <GoogleReviews />

          <div className="text-center mt-8">
            <a href="https://maps.app.goo.gl/pX9M3ZCPCxiRWJNQ8" target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-bbz-text hover:text-bbz-green font-medium transition-colors">
              Voir tous les avis Google <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Vidéo de Présentation */}
      <section id="video-presentation" className="bg-white py-16" aria-labelledby="video-presentation-title">
        <div className="container mx-auto px-4">
          <h2 id="video-presentation-title" className="section-title text-center mx-auto after:mx-auto mb-10">
            Découvrez l'institut en vidéo
          </h2>

          {/* Schema.org JSON-LD pour la vidéo */}
          <script type="application/ld+json">
            {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "VideoObject",
            "name": "Présentation de l'institut Bio Beauté Zen à Montpellier",
            "description": "Valérie vous présente son institut de beauté bio à Montpellier spécialisé dans les soins visage naturels, massages relaxants et épilations douces. Découvrez mon approche éco-responsable et mes produits 100% bio.",
            "thumbnailUrl": "/images/portrait-valerie.jpg",
            "uploadDate": "2025-01-15T10:00:00+01:00",
            "contentUrl": "https://biobeautezen.com/videos/presentation-institut-bio-beaute-zen.mp4",
            "embedUrl": "https://biobeautezen.com/#video-presentation",
            "interactionStatistic": {
              "@type": "InteractionCounter",
              "interactionType": "https://schema.org/WatchAction",
              "userInteractionCount": 0
            },
            "inLanguage": "fr-FR",
            "duration": "PT39S",
            "publisher": {
              "@type": "Organization",
              "name": "Bio Beauté Zen",
              "logo": {
                "@type": "ImageObject",
                "url": "/images/logo-bbz-sans-baseline.png"
              }
            }
          })}
          </script>

          <div className="max-w-sm mx-auto">
            <div className="relative rounded-lg overflow-hidden shadow-2xl bg-gradient-to-br from-bbz-green/20 to-bbz-pink/20 aspect-[9/16]">
              {videoActivated ? (
                <MinimalVideoPlayer
                  src="/videos/presentation-institut-bio-beaute-zen.mp4"
                  poster="/images/portrait-valerie.jpg"
                  autoPlay
                  title="Présentation de Bio Beauté Zen par Valérie"
                  ariaLabel="Présentation de l'institut Bio Beauté Zen par Valérie - Soins visage, massages et épilations bio à Montpellier"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setVideoActivated(true)}
                  className="group absolute inset-0 w-full h-full flex items-center justify-center"
                  aria-label="Lire la vidéo de présentation de l'institut Bio Beauté Zen"
                >
                  <img
                    src="/images/portrait-valerie.jpg"
                    alt="Aperçu de la vidéo de présentation de Bio Beauté Zen"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-[center_5%]"
                  />
                  <span className="relative z-10 flex items-center justify-center w-16 h-16 rounded-full bg-white/90 group-hover:bg-white shadow-lg transition-transform group-hover:scale-110">
                    <svg className="w-7 h-7 text-bbz-green-dark ml-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </span>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>;
};

export default Home;
